// Barrel de telas com code splitting. Cada `lazy()` aqui vira um chunk
// separado no bundle final — o JS de cada rota só baixa quando o usuário
// navega pra ela. Reduz drasticamente o bundle inicial (de ~700 kB pra
// ~200 kB) e melhora o Time to Interactive em redes lentas.
//
// IMPORTANTE: o `import()` precisa estar DENTRO de uma função (loader) pra
// o Webpack tratar como ponto de code splitting. Se chamarmos `import()`
// no topo do módulo, o chunk baixa imediatamente — anula o benefício.
import { lazy } from "react";

// Auto-recuperação de ChunkLoadError: depois de um deploy, o Service Worker
// pode estar servindo um index.html antigo que referencia chunks com hash
// que NÃO EXISTEM mais no servidor. O import() falha, o ErrorBoundary global
// derruba o app inteiro com "Algo deu errado" em todas as telas.
//
// Estratégia padrão da indústria: ao falhar o load do chunk, limpa os caches
// do SW e recarrega a página UMA vez (guard em sessionStorage evita loop
// infinito se o erro persistir por outro motivo).
const importComRetry = (loader) =>
  loader().then((m) => {
    // Chunk carregou — limpa o guard pra próximos deploys poderem se
    // auto-recuperar de novo.
    try { sessionStorage.removeItem("toalugando_chunk_reload"); } catch {}
    return m;
  }).catch(async (err) => {
    const ehChunkError =
      err?.name === "ChunkLoadError" ||
      /loading chunk|loading css chunk|failed to fetch dynamically imported/i.test(err?.message || "");
    const jaRecarregou = sessionStorage.getItem("toalugando_chunk_reload") === "1";
    if (ehChunkError && !jaRecarregou) {
      sessionStorage.setItem("toalugando_chunk_reload", "1");
      try {
        // Derruba caches do SW (versão antiga do shell) antes de recarregar
        if ("caches" in window) {
          const nomes = await caches.keys();
          await Promise.all(nomes.map((n) => caches.delete(n)));
        }
      } catch {}
      window.location.reload();
      // Promise que nunca resolve — página vai recarregar
      return new Promise(() => {});
    }
    throw err;
  });

const named = (loader, name) => lazy(() => importComRetry(loader).then(m => ({ default: m[name] })));

// Pré-carrega o código das telas da barra inferior assim que o app está
// ocioso: a PRIMEIRA ida a cada aba deixava a tela atual sumir e um spinner
// no lugar até o chunk baixar (a "pausa" ao clicar, relato do dono 24/09).
// O Webpack reaproveita o mesmo chunk do `import()` de cima, então não há
// download duplicado. Falha em silêncio (offline, por exemplo).
const ABAS_PRE_CARREGAR = [
  () => import("./screens/home/TelaHome"),
  () => import("./screens/chat/TelaChat"),
  () => import("./screens/publicar/TelaPublicar"),
  () => import("./screens/pedidos/TelaCentralLocacoes"),
  () => import("./screens/perfil/TelaPerfil"),
  // As duas telas que a Central de Locações embute
  () => import("./screens/pedidos/TelaMinhasLocacoes"),
  () => import("./screens/pedidos/TelaPedidoLocador"),
];
export const preCarregarAbas = () => {
  const rodar = () => ABAS_PRE_CARREGAR.forEach((l) => { try { l().catch(() => {}); } catch {} });
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(rodar, { timeout: 4000 });
  } else {
    setTimeout(rodar, 2500);
  }
};

// ─── Auth ──────────────────────────────────────────────────────
export const TelaLogin             = named(() => import("./screens/auth/TelaLogin"), "TelaLogin");
export const TelaRegistro          = named(() => import("./screens/auth/TelaRegistro"), "TelaRegistro");
export const TelaEsqueceuSenha     = named(() => import("./screens/auth/TelaEsqueceuSenha"), "TelaEsqueceuSenha");
export const TelaRedefinirSenha    = named(() => import("./screens/auth/TelaRedefinirSenha"), "TelaRedefinirSenha");
export const TelaSelecionarTipoCadastro = named(() => import("./screens/auth/TelaSelecionarTipoCadastro"), "TelaSelecionarTipoCadastro");

// ─── Legais ────────────────────────────────────────────────────
export const TelaTermos            = named(() => import("./screens/legais/TelaTermos"), "TelaTermos");
export const TelaRegras            = named(() => import("./screens/legais/TelaRegras"), "TelaRegras");
export const TelaPrivacidade       = named(() => import("./screens/legais/TelaPrivacidade"), "TelaPrivacidade");
export const TelaExcluirConta      = named(() => import("./screens/legais/TelaExcluirConta"), "TelaExcluirConta");
export const TelaModeracao         = named(() => import("./screens/legais/TelaModeracao"), "TelaModeracao");
export const TelaEspecificacaoLogistica = named(() => import("./screens/legais/TelaEspecificacaoLogistica"), "TelaEspecificacaoLogistica");

// ─── Home ──────────────────────────────────────────────────────
export const TelaHome              = named(() => import("./screens/home/TelaHome"), "TelaHome");
export const HomeLocador           = named(() => import("./screens/home/HomeLocador"), "HomeLocador");
export const HomeFreteiro          = named(() => import("./screens/home/HomeFreteiro"), "HomeFreteiro");
export const HomeParticular        = named(() => import("./screens/home/HomeParticular"), "HomeParticular");

// ─── Marketplace ───────────────────────────────────────────────
export const TelaDetalhes          = named(() => import("./screens/detalhes/TelaDetalhes"), "TelaDetalhes");
export const TelaDetalhesPacote    = named(() => import("./screens/detalhes/TelaDetalhesPacote"), "TelaDetalhesPacote");
export const TelaBusca             = named(() => import("./screens/busca/TelaBusca"), "TelaBusca");
export const TelaCarrinho          = named(() => import("./screens/carrinho/TelaCarrinho"), "TelaCarrinho");
export const TelaCheckout          = named(() => import("./screens/checkout/TelaCheckout"), "TelaCheckout");
export const TelaFavoritos         = named(() => import("./screens/favoritos/TelaFavoritos"), "TelaFavoritos");
// Vitrines públicas de SEO: /aluguel/<cidade>[/<categoria>] e /categoria/<categoria>
export const TelaVitrine           = named(() => import("./screens/busca/TelaVitrine"), "TelaVitrine");

// ─── Pedidos ───────────────────────────────────────────────────
export const TelaPedidos           = named(() => import("./screens/pedidos/TelaPedidos"), "TelaPedidos");
export const TelaPedidoLocador     = named(() => import("./screens/pedidos/TelaPedidoLocador"), "TelaPedidoLocador");
export const TelaCentralLocacoes   = named(() => import("./screens/pedidos/TelaCentralLocacoes"), "TelaCentralLocacoes");
export const TelaRotaLocador       = named(() => import("./screens/locador/TelaRotaLocador"), "TelaRotaLocador");
export const TelaMinhasLocacoes    = named(() => import("./screens/pedidos/TelaMinhasLocacoes"), "TelaMinhasLocacoes");
export const TelaRastreamento      = named(() => import("./screens/pedidos/TelaRastreamento"), "TelaRastreamento");
export const TelaPortalLocatario   = named(() => import("./screens/pedidos/TelaPortalLocatario"), "TelaPortalLocatario");

// ─── Perfil / Conta ────────────────────────────────────────────
export const TelaPerfil            = named(() => import("./screens/perfil/TelaPerfil"), "TelaPerfil");
export const TelaConfig            = named(() => import("./screens/config/TelaConfig"), "TelaConfig");
export const TelaDashboard         = named(() => import("./screens/dashboard/TelaDashboard"), "TelaDashboard");
export const TelaVerificacao       = named(() => import("./screens/kyc/TelaVerificacao"), "TelaVerificacao");
export const TelaDocumentos        = named(() => import("./screens/documentos/TelaDocumentos"), "TelaDocumentos");

// ─── Locador ───────────────────────────────────────────────────
export const TelaMeusAnuncios      = named(() => import("./screens/locador/TelaMeusAnuncios"), "TelaMeusAnuncios");
export const TelaPrecificacao      = named(() => import("./screens/locador/TelaPrecificacao"), "TelaPrecificacao");
export const TelaInadimplencias    = named(() => import("./screens/locador/TelaInadimplencias"), "TelaInadimplencias");
export const TelaCalendarioLocador = named(() => import("./screens/locador/TelaCalendarioLocador"), "TelaCalendarioLocador");
export const TelaExtensoesPendentes = named(() => import("./screens/locador/TelaExtensoesPendentes"), "TelaExtensoesPendentes");
export const TelaPerfilPublico     = named(() => import("./screens/locador/TelaPerfilPublico"), "TelaPerfilPublico");
export const TelaMeusPacotes       = named(() => import("./screens/locador/TelaMeusPacotes"), "TelaMeusPacotes");
export const TelaPerguntasLocador  = named(() => import("./screens/locador/TelaPerguntasLocador"), "TelaPerguntasLocador");
export const TelaCadastroLocadora  = named(() => import("./screens/locador/TelaCadastroLocadora"), "TelaCadastroLocadora");
export const TelaConfigLocador     = named(() => import("./screens/locador/TelaConfigLocador"), "TelaConfigLocador");
export const TelaMeusCupons        = named(() => import("./screens/locador/TelaMeusCupons"), "TelaMeusCupons");
export const TelaIntegracaoGestao  = named(() => import("./screens/locador/TelaIntegracaoGestao"), "TelaIntegracaoGestao");
export const TelaEntregasLocador   = named(() => import("./screens/locador/TelaEntregasLocador"), "TelaEntregasLocador");
export const TelaCompletarLote     = named(() => import("./screens/locador/TelaCompletarLote"), "TelaCompletarLote");
export const TelaImportarCatalogo  = named(() => import("./screens/locador/TelaImportarCatalogo"), "TelaImportarCatalogo");
export const TelaDevolucoesLocador = named(() => import("./screens/locador/TelaDevolucoesLocador"), "TelaDevolucoesLocador");

// ─── Freteiro ──────────────────────────────────────────────────
export const TelaPortalFreteiro    = named(() => import("./screens/freteiro/TelaPortalFreteiro"), "TelaPortalFreteiro");
export const TelaFreteiroWelcome   = named(() => import("./screens/freteiro/TelaFreteiroWelcome"), "TelaFreteiroWelcome");
export const TelaFreteiroPermissoes = named(() => import("./screens/freteiro/TelaFreteiroPermissoes"), "TelaFreteiroPermissoes");
export const TelaDashboardFreteiro = named(() => import("./screens/freteiro/TelaDashboardFreteiro"), "TelaDashboardFreteiro");
export const TelaMeusVeiculos      = named(() => import("./screens/freteiro/TelaMeusVeiculos"), "TelaMeusVeiculos");
export const TelaFreteDisponivel   = named(() => import("./screens/freteiro/TelaFreteDisponivel"), "TelaFreteDisponivel");
export const TelaCadastroFreteiro  = named(() => import("./screens/freteiro/TelaCadastroFreteiro"), "TelaCadastroFreteiro");
export const TelaRotaFreteiro      = named(() => import("./screens/freteiro/TelaRotaFreteiro"), "TelaRotaFreteiro");
export const TelaMeusStrikes       = named(() => import("./screens/freteiro/TelaMeusStrikes"), "TelaMeusStrikes");

// ─── Financeiro ────────────────────────────────────────────────
export const TelaFinanceiro        = named(() => import("./screens/financeiro/TelaFinanceiro"), "TelaFinanceiro");
export const TelaMeusNiveis        = named(() => import("./screens/financeiro/TelaMeusNiveis"), "TelaMeusNiveis");
export const TelaCarteiraLocador   = named(() => import("./screens/financeiro/TelaCarteira"), "TelaCarteiraLocador");

// ─── Empresa ───────────────────────────────────────────────────
export const TelaMinhasEmpresas    = named(() => import("./screens/empresas/TelaMinhasEmpresas"), "TelaMinhasEmpresas");
export const TelaCadastroLocatariaPJ = named(() => import("./screens/empresas/TelaCadastroLocatariaPJ"), "TelaCadastroLocatariaPJ");
export const TelaCadastroEmpresa   = named(() => import("./screens/empresas/TelaCadastroEmpresa"), "TelaCadastroEmpresa");

// ─── Indicação / Cashback ──────────────────────────────────────
export const TelaConvideAmigos     = named(() => import("./screens/indicacao/TelaConvideAmigos"), "TelaConvideAmigos");

// ─── Outros ────────────────────────────────────────────────────
export const TelaAvaliacoes        = named(() => import("./screens/avaliacoes/TelaAvaliacoes"), "TelaAvaliacoes");
export const TelaSuporte           = named(() => import("./screens/suporte/TelaSuporte"), "TelaSuporte");
export const TelaChat              = named(() => import("./screens/chat/TelaChat"), "TelaChat");
export const TelaNotificacoes      = named(() => import("./screens/notificacoes/TelaNotificacoes"), "TelaNotificacoes");
export const TelaPublicar          = named(() => import("./screens/publicar/TelaPublicar"), "TelaPublicar");

// ─── Admin ─────────────────────────────────────────────────────
export const TelaConfigAdmin       = named(() => import("./screens/admin/TelaConfigAdmin"), "TelaConfigAdmin");
export const TelaDevolucoesAdmin   = named(() => import("./screens/admin/TelaDevolucoesAdmin"), "TelaDevolucoesAdmin");
export const TelaAuditLogAdmin     = named(() => import("./screens/auditoria/TelaAuditLogAdmin"), "TelaAuditLogAdmin");
